/**
 * Button component - Premium Apple-inspired design
 * 2026 Premium styling with smooth transitions, haptic feedback, and proper touch targets
 * Performance optimized with React.memo
 */

import React, { memo, useCallback, useState } from 'react';
import { triggerHaptic } from '@/lib/haptics';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button visual variant */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'destructive' | 'warm' | 'success' | 'danger' | 'premium';
  /** Button size - all meet 44px minimum touch target */
  size?: 'sm' | 'md' | 'lg';
  /** Show loading spinner */
  loading?: boolean;
  /** Optional icon */
  icon?: React.ReactNode;
  /** Icon position relative to text */
  iconPosition?: 'left' | 'right';
  /** Stretch to full width */
  fullWidth?: boolean;
  /** Trigger haptic feedback on press */
  haptic?: boolean;
  children: React.ReactNode;
}

// Pre-computed base styles to avoid string processing on every render
const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-button motion-safe:transition-all duration-150 ease-out touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200/80 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

export const Button = memo(function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  haptic = true,
  disabled,
  className = '',
  children,
  onClick,
  ...props
}: ButtonProps) {
  const [isPressed, setIsPressed] = useState(false);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (haptic && !disabled && !loading) triggerHaptic();
    onClick?.(e);
  }, [haptic, disabled, loading, onClick]);

  const handlePressStart = useCallback(() => {
    if (!disabled && !loading) {
      setIsPressed(true);
    }
  }, [disabled, loading]);

  const handlePressEnd = useCallback(() => {
    setIsPressed(false);
  }, []);

  // Premium color variants with refined Apple HIG 2026 styling
  // Multi-layer shadows, subtle gradients, refined interactions
  const variants = {
    // Filled blue - primary actions
    primary: `bg-gradient-to-b from-[#0A84FF] via-primary-500 to-primary-600 text-white
              shadow-[0_0.5px_1px_rgba(0,0,0,0.03),0_1px_3px_rgba(0,122,255,0.15),0_3px_8px_rgba(0,122,255,0.12),inset_0_0.5px_0_rgba(255,255,255,0.2)]
              hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,122,255,0.18),0_8px_20px_rgba(0,122,255,0.12)] hover:-translate-y-px`,
    // Outlined white - secondary actions
    secondary: `bg-white/98 text-gray-800 border border-black/[0.05]
                shadow-[0_0.5px_1px_rgba(0,0,0,0.02),0_1px_3px_rgba(0,0,0,0.03),inset_0_0.5px_0_rgba(255,255,255,0.9)]
                hover:bg-gray-50/90 hover:border-black/[0.07] hover:shadow-[0_1px_2px_rgba(0,0,0,0.03),0_3px_8px_rgba(0,0,0,0.04)] hover:-translate-y-px`,
    // Text-only blue - tertiary actions
    tertiary: 'text-primary-500 hover:text-primary-600 hover:bg-primary-50/40 active:bg-primary-50/60',
    // Blue with subtle background - ghost actions
    ghost: 'text-primary-500 hover:bg-primary-50/60 hover:text-primary-600 active:bg-primary-100/60',
    // Red destructive - delete/remove actions
    destructive: `bg-gradient-to-b from-red-500 via-red-500 to-red-600 text-white
                  shadow-[0_0.5px_1px_rgba(0,0,0,0.03),0_1px_3px_rgba(239,68,68,0.15),0_3px_8px_rgba(239,68,68,0.12),inset_0_0.5px_0_rgba(255,255,255,0.2)]
                  hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(239,68,68,0.18),0_8px_20px_rgba(239,68,68,0.12)] hover:-translate-y-px`,
    // Legacy alias for destructive
    danger: `bg-gradient-to-b from-red-500 via-red-500 to-red-600 text-white
             shadow-[0_0.5px_1px_rgba(0,0,0,0.03),0_1px_3px_rgba(239,68,68,0.15),0_3px_8px_rgba(239,68,68,0.12),inset_0_0.5px_0_rgba(255,255,255,0.2)]
             hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(239,68,68,0.18),0_8px_20px_rgba(239,68,68,0.12)] hover:-translate-y-px`,
    // Teal warm - special warm actions
    warm: `bg-gradient-to-b from-secondary-400 via-secondary-500 to-secondary-600 text-white
           shadow-[0_0.5px_1px_rgba(0,0,0,0.03),0_1px_3px_rgba(20,184,166,0.15),0_3px_8px_rgba(20,184,166,0.12),inset_0_0.5px_0_rgba(255,255,255,0.2)]
           hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(20,184,166,0.18),0_8px_20px_rgba(20,184,166,0.12)] hover:-translate-y-px`,
    // Green success - confirmation actions
    success: `bg-gradient-to-b from-emerald-500 via-emerald-500 to-emerald-600 text-white
              shadow-[0_0.5px_1px_rgba(0,0,0,0.03),0_1px_3px_rgba(16,185,129,0.15),0_3px_8px_rgba(16,185,129,0.12),inset_0_0.5px_0_rgba(255,255,255,0.2)]
              hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(16,185,129,0.18),0_8px_20px_rgba(16,185,129,0.12)] hover:-translate-y-px`,
    // Premium gradient - hero CTAs
    premium: `bg-gradient-to-b from-[#38A3F5] via-primary-500 to-primary-600 text-white
              shadow-[0_0.5px_1px_rgba(0,0,0,0.04),0_2px_6px_rgba(0,122,255,0.2),0_6px_16px_rgba(0,122,255,0.15),inset_0_0.5px_0_rgba(255,255,255,0.25)]
              hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_8px_20px_rgba(0,122,255,0.22),0_16px_36px_rgba(0,122,255,0.12)] hover:-translate-y-0.5`,
  };

  // All sizes meet Apple's 44pt minimum touch target
  const sizes = {
    sm: 'px-4 py-2.5 text-body-sm gap-1.5 min-h-[44px]',
    md: 'px-5 py-2.5 text-body gap-2 min-h-[44px]',
    lg: 'px-6 py-3.5 text-base gap-2 min-h-[52px]',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-[18px] h-[18px]',
    lg: 'w-5 h-5',
  };

  const widthClass = fullWidth ? 'w-full' : '';
  // Apple-like subtle press feedback: less aggressive scaling, subtle shadow reduction
  const pressedClass = isPressed ? 'scale-[0.98] translate-y-px shadow-sm brightness-[0.98]' : '';

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${pressedClass} ${className}`}
      disabled={disabled || loading}
      onClick={handleClick}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressEnd}
      aria-busy={loading}
      {...props}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <svg className={`motion-safe:animate-spin ${iconSizes[size]}`} viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Loading...</span>
        </span>
      ) : (
        <>
          {icon && iconPosition === 'left' && <span className={`flex-shrink-0 ${iconSizes[size]}`}>{icon}</span>}
          <span>{children}</span>
          {icon && iconPosition === 'right' && <span className={`flex-shrink-0 ${iconSizes[size]}`}>{icon}</span>}
        </>
      )}
    </button>
  );
});

